import React from "react";
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack,
  Button,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import pdf from '../assets/Indy_Golf_Den_FAQ_Branded.pdf';

const FAQ_SECTIONS = [
  {
    title: "Arrival & Location",
    items: [
      {
        q: "What does the building look like?",
        a: "Our building looks like a normal residential house, but it is actually a commercial building—so don’t let it fool you. It has white vinyl siding with black trim and a black roof.",
      },
      {
        q: "How early can I arrive?",
        a: "Please arrive no more than 5 minutes early. We provide a 10-minute grace period at the end of your session to account for setup time.",
      },
    ],
  },
  {
    title: "Parking & Access",
    items: [
      {
        q: "Where exactly should I park?",
        a: "Our driveway is double-wide next to our neighbor’s single-wide driveway. You may park in the angled spots near the building or in the vertical spot at the end near the neighbor’s garage.",
      },
      {
        q: "Which entrance do I use?",
        a: "Use the side entrance with the keypad, enter through the kitchen, then walk down the hallway to the simulator room.",
      },
    ],
  },
  {
    title: "Booking & Timing",
    items: [
      {
        q: "What if I want to extend my booking?",
        a: "Book another time slot on our website if available. Otherwise, you must leave on time.",
      },
    ],
  },
  {
    title: "Facility & Guests",
    items: [
      {
        q: "Are there staff members on site?",
        a: "No. We are a self-check-in facility similar to Airbnb. Contact us by text or email if needed.",
      },
      {
        q: "Can I bring non-player guests?",
        a: "Yes. Maximum occupancy is four people total. Everyone must be included in the booking and sign the waiver.",
      },
    ],
  },
  {
    title: "Food & Drinks",
    items: [
      {
        q: "Can I bring my own drinks or alcohol?",
        a: "Yes, we are BYOB. Guests are responsible for any spills or damage.",
      },
    ],
  },
  {
    title: "Simulator & Technology",
    items: [
      {
        q: "What if the monitor is not reading my shots?",
        a: "Restart all software. If the issue persists, contact staff by text or email.",
      },
      {
        q: "Is the TV Wi-Fi enabled?",
        a: "Yes, it supports streaming apps (with your login) and screen casting.",
      },
      {
        q: "Is there music or a sound system?",
        a: "Yes, a high-quality Sonos Bluetooth speaker system is available.",
      },
    ],
  },
  {
    title: "Gameplay Options",
    items: [
      {
        q: "Is there putting?",
        a: "Yes, with realistic speed, slopes, and green reading.",
      },
      {
        q: "What if I am left-handed?",
        a: "Left-handed players are limited to irons, wedges, and putting due to space constraints.",
      },
      {
        q: "Can I play only nine holes?",
        a: "Yes. Choose 3, 9, 18, or custom hole selections before starting.",
      },
    ],
  },
  {
    title: "Results & Statistics",
    items: [
      {
        q: "Can I get a copy of my session results?",
        a: "Yes via the Uneekor (U-N-E-E-K-O-R) driving range. GSPro is typically used for course play, but Uneekor provides more detailed practice feedback and allows emailing results.",
      },
      {
        q: "Which statistics can be tracked?",
        a: "Our system tracks virtually every meaningful golf metric including ball speed, spin, club path, face angle, carry distance, dispersion, and more.",
      },
    ],
  },
  {
    title: "Departure & Security",
    items: [
      {
        q: "Do I need to set the alarm when I leave?",
        a: "No. Just lock the side door deadbolt. Staff handles the alarm remotely.",
      },
    ],
  },
];

const FaqPage = () => {
  // If you add the PDF to /public/docs/Indy_Golf_Den_FAQ.pdf, this path will work in production:
  const pdfHref = pdf;

  return (
    <Box sx={{ backgroundColor: "#0f231a", minHeight: "100vh", py: { xs: 6, md: 10 } }}>
      <Container maxWidth="md">
        <Paper
          elevation={6}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid rgba(230,220,187,0.18)",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
            backdropFilter: "blur(6px)",
          }}
        >
          <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: "lulo-one",
                    color: "#d89936",
                    fontSize: { xs: "2rem", md: "2.6rem" },
                    lineHeight: 1.1,
                  }}
                >
                  FAQ
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#e6dcbb", opacity: 0.9, mt: 1, fontFamily: "Roboto" }}
                >
                  Quick answers for arrival, parking, booking, and simulator setup.
                </Typography>
              </Box>

              <Button
                component="a"
                href={pdfHref}
                target="_blank"
                rel="noreferrer"
                startIcon={<PictureAsPdfIcon />}
                variant="outlined"
                sx={{
                  borderColor: "rgba(230,220,187,0.45)",
                  color: "#e6dcbb",
                  borderRadius: 999,
                  textTransform: "none",
                  fontFamily: "Roboto",
                  "&:hover": { borderColor: "rgba(230,220,187,0.8)" },
                }}
              >
                View / Download PDF
              </Button>
            </Stack>
          </Box>

          <Divider sx={{ borderColor: "rgba(230,220,187,0.14)" }} />

          <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 1, md: 2 } }}>
            {FAQ_SECTIONS.map((section) => (
              <Box key={section.title} sx={{ mb: 1.5 }}>
                <Typography
                  sx={{
                    px: { xs: 1, md: 2 },
                    pt: 2,
                    pb: 1,
                    fontFamily: "lulo-one",
                    color: "#d89936",
                    fontSize: "0.95rem",
                    opacity: 0.95,
                  }}
                >
                  {section.title}
                </Typography>

                {section.items.map((item) => (
                  <Accordion
                    key={item.q}
                    disableGutters
                    elevation={0}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.02)",
                      border: "1px solid rgba(230,220,187,0.12)",
                      borderRadius: 2,
                      mb: 1,
                      overflow: "hidden",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#e6dcbb" }} />}
                      sx={{
                        px: { xs: 2, md: 2.5 },
                        "& .MuiAccordionSummary-content": { my: 1.2 },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Roboto",
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: { xs: "1rem", md: "1.05rem" },
                        }}
                      >
                        {item.q}
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pb: 2 }}>
                      <Typography
                        sx={{
                          fontFamily: "Roboto",
                          color: "#e6dcbb",
                          opacity: 0.92,
                          lineHeight: 1.6,
                        }}
                      >
                        {item.a}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ))}

            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                color: "rgba(230,220,187,0.75)",
                fontFamily: "Roboto",
                py: 3,
              }}
            >
              Last updated: 2026
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default FaqPage;